// prs.js — personal records, one rule set for the store (completeSession,
// edits, deletes, restores) and for sync (docToState).
//
// A PR is the best estimated 1RM per exercise on the EFFECTIVE load: body
// weight ± the logged load for bodyweight / weighted-bodyweight / assisted
// lifts (analytics.setLoad). Warm-ups, myo / rest-pause / cluster mini-sets,
// timed holds and cardio never set a PR. PR entries keep `weight` = effective
// load and `added` = the logged number when they differ.
//
// Bump PRS_VERSION whenever these rules change: the store rebuilds stored PRs
// from history once when it loads data with an older version.

import { e1rm, exerciseKind, setLoad, isStrengthSet } from './analytics.js';

export const PRS_VERSION = 4;

const localDay = (ts) => { const d = new Date(ts); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function kindLookup(exercises) {
  const byId = new Map();
  for (const e of exercises || []) if (e && e.id != null) byId.set(e.id, e);
  const memo = new Map();
  return (id) => {
    let k = memo.get(id);
    if (!k) { k = exerciseKind(byId.get(id) || { id, unit: undefined }); memo.set(id, k); }
    return k;
  };
}

// Best set per exercise of one workout. Cached per workout object together
// with what it depended on: the kind of each exercise it used and, for
// bodyweight lifts logged without a body-weight snapshot, the body weight that
// was looked up. A new weigh-in or an unrelated library edit keeps the cache.
const cache = new WeakMap();
const kindKey = (k) => k.load + '/' + k.track;
function bestsOf(state, ss, kindOf) {
  const c = cache.get(ss);
  if (c && c.kinds.every(([id, key]) => kindKey(kindOf(id)) === key) && c.bws.every(([at, bw]) => setLoad(state, { w: 0 }, { load: 'bodyweight' }, at) === bw)) return c.b;
  const b = {};
  const kinds = new Map(), bws = [];
  for (const st of ss.sets || []) {
    if (!isStrengthSet(st)) continue;
    const kind = kindOf(st.exerciseId);
    kinds.set(st.exerciseId, kindKey(kind));
    if (kind.track !== 'reps') continue;
    const at = st.ts || ss.startedAt;
    const eff = setLoad(state, st, kind, at);
    if (kind.load !== 'weighted' && !(st.bw > 0)) bws.push([at, setLoad(state, { w: 0 }, { load: 'bodyweight' }, at)]);
    if (!(eff > 0)) continue;
    const v = e1rm(eff, st.reps);
    const cur = b[st.exerciseId];
    if (!cur || v > cur.e1rm) b[st.exerciseId] = { weight: eff, reps: st.reps, e1rm: v, ...(eff !== st.w ? { added: +st.w || 0 } : {}) };
  }
  cache.set(ss, { kinds: [...kinds], bws, b });
  return b;
}

/** Best set per exercise in one session: { [exerciseId]: { weight, reps, e1rm, added? } }. */
export function sessionBests(state, ss, kindOf = kindLookup(state && state.exercises)) {
  return bestsOf(state || {}, ss, kindOf);
}

/** PRs from every completed session in state.sessions (or `sessions`). */
export function computePRs(state, sessions = state && state.sessions) {
  const st = state || {};
  const kindOf = kindLookup(st.exercises);
  const best = {};
  for (const ss of sessions || []) {
    if (!ss || !ss.completedAt) continue;
    const b = bestsOf(st, ss, kindOf);
    for (const id of Object.keys(b)) {
      const v = b[id];
      if (!best[id] || v.e1rm > best[id].e1rm) best[id] = { exerciseId: id, ...v, date: localDay(ss.startedAt) };
    }
  }
  return Object.values(best);
}

/** Warm the per-session cache in slices (sync applies big docs without long tasks). */
export async function warmPRs(state, sessions, slice) {
  const kindOf = kindLookup(state && state.exercises);
  for (const ss of sessions || []) {
    if (!ss || !ss.completedAt) continue;
    bestsOf(state || {}, ss, kindOf);
    if (slice) { const y = slice(); if (y) await y; }
  }
}
